/* eslint-disable consistent-return */
import SimpleSchema from 'simpl-schema';
import { map } from 'lodash';
import { NodeSchemaMonitor, NodeSchemaOut, NodeSchemaIn, NodeSchemaToSave } from './NodeSchema';
import { STATUS_TYPES } from './taskConstants';
import requiredIf from '../../modules/schema-conditional-required';

/**
 * The base fields for a task.
 *
 * @typedef {object} TaskBaseFields
 * @property {string} name - The name for the task. UNIQUE.
 * @property {string} label - A human readable name for the task.
 * @property {string} group - The for group the task. Human readable.
 * @property {string} comments - Some comments about the task status.
 * @property {Link[]} links - The links array for the task.
 * @property {boolean} ready - A flag to check if the task is ready to use.
 */

/**
 * A task for the tasklist.
 *
 * @typedef {object} TaskIn
 * @augments TaskBaseFields
 * @property {NodeSchemaIn[]} nodes - The nodes array for the task.
 */

/**
 * A task to run.
 *
 * @typedef {object} TaskSchemaOut
 * @augments TaskBaseFields
 * @property {string} _id - The task id.
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - The date when it finished.
 * @property {NodeSchemaOut[]} nodes - The nodes array for the task.
 */

/**
 * A task for the monitor.
 *
 * @typedef {object} TaskSchemaToSave
 * @augments TaskBaseFields
 * @property {string} _id - The task id.
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - The date when it finished.
 * @property {string} [startedAt] - The date when it started running.
 * @property {string} [finishedAt] - The date when it finished.
 * @property {string} [processId] - The id of the running task. Not the same as the task Id.
 * @property {string} [status] - The status for the running task. One of [ERROR,CANCELED,DONE,INPROGRESS,PAUSED].
 * @property {number} [progress] - The progress of the running task.
 * @property {NodeSchemaToSave[]} nodes - The nodes array for the task.
 */

/**
 * A task to save in Mongo.
 *
 * @typedef {object} TaskSchemaToSave
 * @augments TaskBaseFields
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - The date when it finished.
 * @property {string} startedAt - The date when it started running.
 * @property {string} finishedAt - The date when it finished.
 * @property {string} processId - The id of the running task. Not the same as the task Id.
 * @property {string} status - The status for the running task. One of [ERROR,CANCELED,DONE,INPROGRESS,PAUSED].
 * @property {number} progress - The progress of the running task.
 * @property {NodeSchemaToSave[]} nodes - The nodes array for the task.
 */

/**
 * A link in a task.
 *
 * @typedef {object} Link
 * @property {string} source - The id of te source node.
 * @property {string} target - The id of the target node.
 */

const LinkSchema = new SimpleSchema({
  source: {
    type: String,
    label: 'The id of the source node'
  },
  target: {
    type: String,
    label: 'The id of the target node'
  }
});

export const TaskSchemaMonitor = new SimpleSchema({
  _id: {
    type: String,
    label: 'The ID the task to run'
  },
  owner: {
    type: String,
    label: 'The ID of the user this task belongs to.'
  },
  createdAt: {
    type: String,
    label: 'The date this task was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this task was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  startedAt: {
    type: String,
    label: 'The date when it started running',
    optional: true
  },
  finishedAt: {
    type: String,
    label: 'The date when it finished',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: STATUS_TYPES.DONE === this.field('status').value,
        schemaInstance: this
      });
    }
  },
  name: {
    type: String,
    label: 'A name to identify the task.'
  },
  label: {
    type: String,
    label: 'A human readable name for the task'
  },
  group: {
    type: String,
    label: 'The for group the task. Human readable.'
  },
  comments: {
    type: String,
    label: 'Some comments about the status of the task',
    optional: true
  },
  status: {
    type: String,
    label: 'The status for the running subtask',
    allowedValues: map(STATUS_TYPES.properties, 'value')
  },
  progress: {
    type: String,
    label: 'The progress of the running subtask.'
  },
  processId: {
    type: String,
    label: 'The ID of the running task in backend'
  },
  links: {
    type: Array,
    label: 'The links array for the task'
  },
  'links.$': {
    type: LinkSchema,
    label: 'A link in a task'
  },
  nodes: {
    type: Array,
    label: 'The nodes array for the task.',
    minCount: 1
  },
  'nodes.$': {
    type: NodeSchemaMonitor,
    label: 'A node in a task'
  },
  deleted: {
    type: String,
    label: 'The date the task was deleted.',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  ready: {
    type: Boolean,
    label: 'Flag that checks if the task is ready'
  }
});

const TaskSchemaToSave = TaskSchemaMonitor.omit('nodes', '_id');

TaskSchemaToSave.extend({
  startedAt: {
    type: String,
    label: 'The date when it started running',
    optional: true
  },
  finishedAt: {
    type: String,
    label: 'The date when it finished',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: STATUS_TYPES.DONE === this.field('status').value,
        schemaInstance: this
      });
    }
  },
  status: {
    type: String,
    label: 'The status for the running subtask',
    allowedValues: map(STATUS_TYPES.properties, 'value'),
    optional: true
  },
  progress: {
    type: Number,
    label: 'The progress of the running subtask.',
    optional: true
  },
  processId: {
    type: String,
    label: 'The ID of the running task in backend',
    optional: true
  },
  nodes: {
    type: Array,
    label: 'The nodes array for the task.',
    minCount: 0 // Provisional devolver a 1
  },
  'nodes.$': {
    type: NodeSchemaToSave,
    label: 'A node in a task'
  }
});

const TaskSchemaOut = TaskSchemaMonitor.omit(
  'progress',
  'status',
  'finishedAt',
  'startedAt',
  'nodes'
);

TaskSchemaOut.extend({
  nodes: {
    type: Array,
    label: 'The nodes array for the task.',
    minCount: 1
  },
  'nodes.$': {
    type: NodeSchemaOut,
    label: 'A node in a task'
  },
  processId: {
    type: String,
    label: 'The progress of the running subtask.',
    optional: true
  }
});

const TaskSchemaIn = TaskSchemaOut.omit(
  'nodes',
  '_id',
  'createdAt',
  'updatedAt',
  'owner',
  'deleted',
  'processId'
);

TaskSchemaIn.extend({
  nodes: {
    type: Array,
    label: 'The nodes array for the task.',
    minCount: 1
  },
  'nodes.$': {
    type: NodeSchemaIn,
    label: 'A node in a task'
  }
});

export { TaskSchemaIn, TaskSchemaOut, TaskSchemaToSave };
